import { Injectable } from '@angular/core';
import { Column } from '../models/column';
import { Section } from '../models/section';
import { GroupedSection, GroupSectionFactory } from '../models/grouped-section';
import { isDetailSection } from '../utils/is-detail-section';
import { isSummarySection } from '../utils/is-summary-section';

export interface SmallSection extends Pick<
  Section,
  | 'sectionId'
  | 'parentSectionId'
  | 'type'
  | 'groupTemplateRefName'
  | 'groupValidityRules'
  | 'formControlOptions'
  | 'targetedControlOptions'
  | 'groupFormControlOptions'
  | 'GroupComponent'
  | 'groupComponentConfig'
  | 'Component'
  | 'componentConfig'
  | 'hide'
  | 'required'
  | 'linkGenerator'
  | 'usageTarget'
> {
  title?: string;
  columns?: Array<number | Column>;
}

interface GroupedSectionMap<T> {
  [key: number]: GroupedSection<T>;
}

const makeNewEntry = <T>(parentSectionId: number, section: T, position = 0): GroupedSection<T> => {
  // when position is given by BE, it should transparently take the right value
  // meanwhile we use index
  return {
    parentSectionId,
    position,
    sections: [section],
  };
};

const mergeEntry = <T>(currentEntry: GroupedSection<T>, nextSection: T): GroupedSection<T> => {
  const nextSections = [...currentEntry.sections, nextSection];
  return { ...currentEntry, sections: nextSections };
};

/**
 * Returns array of GroupedSection<Section>
 * @param sections
 */
export const makeGroupedSections = <I extends SmallSection>(sections: I[]): Array<GroupedSection<SmallSection>> => {
  // sort function
  const byPosition = (a: GroupedSection<SmallSection>, b: GroupedSection<SmallSection>) => a.position - b.position;
  const isDetailSectionAndNotSummary = (section: I) => isDetailSection(section) && !isSummarySection(section);
  const groupedSections: GroupedSectionMap<SmallSection> = sections
    // we group only sections of type details (widget are handled in different places)
    .filter(isDetailSectionAndNotSummary)
    .reduce(mergeByParentSectionId, {});
  // sorted by position (currently index, but actual value should also be provided by BE)
  return Object.values(groupedSections).map(GroupSectionFactory).sort(byPosition);
};

/**
 * Merge sections by parentSectionId
 */
export const mergeByParentSectionId = (
  accumulated: GroupedSectionMap<SmallSection>,
  section: SmallSection,
  index: number
): GroupedSectionMap<SmallSection> => {
  const { parentSectionId } = section;
  const existingEntry: GroupedSection<SmallSection> = accumulated[parentSectionId];

  let nextEntry = !!existingEntry ? mergeEntry(existingEntry, section) : makeNewEntry(parentSectionId, section, index);

  const isParent = parentSectionId === section.sectionId;
  if (isParent) {
    const title = section.title;
    const templateRefName = section.groupTemplateRefName;
    const Component = section.GroupComponent;
    const componentConfig = section.groupComponentConfig;
    const formControlOptions = section.groupFormControlOptions;
    const targetedControlOptions = section.targetedControlOptions;
    const validityRules = section.groupValidityRules;
    const linkGenerator = section.linkGenerator;
    const required = section.required;
    const usageTarget = section.usageTarget;
    nextEntry = {
      ...nextEntry,
      title,
      templateRefName,
      validityRules,
      Component,
      componentConfig,
      formControlOptions,
      targetedControlOptions,
      originalSection: section,
      linkGenerator,
      required,
      usageTarget,
    };
  }
  // next entry
  return { ...accumulated, [parentSectionId]: nextEntry };
};

@Injectable()
export class SubsectionMakerService {
  /**
   * Returns array of GroupedSection<Section>
   * @param sections
   */
  makeGroupedSections<I extends SmallSection>(sections: I[]): Array<GroupedSection<SmallSection>> {
    return makeGroupedSections(sections);
  }
}
